import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Search, MapPin, Phone, Mail, UserCheck, TrendingUp } from "lucide-react";
import { AdminLayout } from "./AdminLayout";
import { StatusBadge } from "../../components/StatusBadge";
import { api, euro, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const STATUSES = ["new", "awaiting_payment", "paid", "searching_partner", "partner_assigned", "appointment_pending", "appointment_confirmed", "in_progress", "completed", "cancelled", "disputed"];

export default function AdminOrders() {
  const [orders, setOrders] = useState(null);
  const [filters, setFilters] = useState({ status: "", payment_status: "", q: "" });
  const [selected, setSelected] = useState(null);
  const [matches, setMatches] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/admin/bookings", { params }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/bookings/${id}`);
    setSelected(data);
    setMatches(null);
  };

  const loadMatches = async () => {
    const { data } = await api.get(`/admin/bookings/${selected.id}/match`);
    setMatches(data);
  };

  const assign = async (partnerId) => {
    setBusy(true);
    try {
      await api.post(`/admin/bookings/${selected.id}/assign`, { partner_id: partnerId });
      toast.success("Partner toegewezen");
      const { data } = await api.get(`/bookings/${selected.id}`);
      setSelected(data); setMatches(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  return (
    <AdminLayout>
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-slate-900">Opdrachten</h1>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input placeholder="Zoek op nummer, naam of e-mail" className="pl-9" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="orders-search" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilters({ ...filters, status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-[200px]" data-testid="orders-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle statussen</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.payment_status || "all"} onValueChange={(v) => setFilters({ ...filters, payment_status: v === "all" ? "" : v })}>
          <SelectTrigger className="w-[180px]" data-testid="orders-filter-payment"><SelectValue placeholder="Betaling" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle betalingen</SelectItem>
            <SelectItem value="pending">In afwachting</SelectItem>
            <SelectItem value="paid">Betaald</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {orders === null ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-slate-400" /></div>
      ) : (
        <div className="mt-6 bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
                <tr>
                  <th className="text-left px-4 py-3">Nr</th>
                  <th className="text-left px-4 py-3">Klant</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Herstelling</th>
                  <th className="text-left px-4 py-3 hidden md:table-cell">Locatie</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3 hidden sm:table-cell">Betaling</th>
                  <th className="text-right px-4 py-3">Prijs</th>
                  <th className="text-right px-4 py-3 hidden lg:table-cell">Marge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {orders.map((o) => (
                  <tr key={o.id} onClick={() => openDetail(o.id)} className="hover:bg-slate-50 cursor-pointer" data-testid={`admin-order-row-${o.booking_number}`}>
                    <td className="px-4 py-3 font-semibold text-slate-900">{o.booking_number}</td>
                    <td className="px-4 py-3">{o.customer.first_name} {o.customer.last_name}</td>
                    <td className="px-4 py-3 hidden sm:table-cell">{o.service_name} ({o.quantity}x)</td>
                    <td className="px-4 py-3 hidden md:table-cell text-slate-500">{o.location.postal_code} {o.location.city}</td>
                    <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                    <td className="px-4 py-3 hidden sm:table-cell"><StatusBadge status={o.payment_status} /></td>
                    <td className="px-4 py-3 text-right font-medium">{euro(o.price)}</td>
                    <td className="px-4 py-3 text-right hidden lg:table-cell text-green-700 font-medium">{euro(o.margin)}</td>
                  </tr>
                ))}
                {orders.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">Geen opdrachten gevonden.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(v) => !v && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-order-detail">
          {selected && (
            <>
              <DialogHeader><DialogTitle className="flex items-center gap-3">{selected.booking_number} <StatusBadge status={selected.status} /> <StatusBadge status={selected.payment_status} /></DialogTitle></DialogHeader>

              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div className="rounded-lg bg-slate-50 p-4 space-y-1">
                  <p className="font-semibold text-slate-900">{selected.customer.first_name} {selected.customer.last_name}</p>
                  <p className="flex items-center gap-1 text-slate-600"><MapPin size={14} /> {selected.location.street} {selected.location.house_number}, {selected.location.postal_code} {selected.location.city}</p>
                  <p className="flex items-center gap-1 text-slate-600"><Phone size={14} /> {selected.customer.phone}</p>
                  <p className="flex items-center gap-1 text-slate-600"><Mail size={14} /> {selected.customer.email}</p>
                </div>
                <div className="rounded-lg bg-slate-50 p-4 space-y-1">
                  <Row label="Herstelling" value={`${selected.service_name} (${selected.quantity}x)`} />
                  <Row label="Materiaal" value={`${selected.material} · ${selected.item_type}`} />
                  <Row label="Voorkeur" value={selected.appointment_preference} />
                  <Row label="Hersteller" value={selected.partner_name || "—"} />
                </div>
              </div>

              {/* Margin */}
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 grid grid-cols-3 gap-2 text-center">
                <div><p className="text-xs text-slate-500">Klant betaalt</p><p className="font-heading font-bold text-slate-900">{euro(selected.price)}</p></div>
                <div><p className="text-xs text-slate-500">Partnervergoeding</p><p className="font-heading font-bold text-orange-700">{euro(selected.partner_fee)}</p></div>
                <div><p className="text-xs text-slate-500">Brutomarge</p><p className="font-heading font-bold text-emerald-700 flex items-center justify-center gap-1"><TrendingUp size={14} /> {euro(selected.margin)}</p></div>
              </div>

              {selected.photos?.length > 0 && (
                <div className="flex gap-2 flex-wrap">{selected.photos.map((p) => <img key={p.id} src={fileUrl(p.path)} alt="" className="h-20 w-20 object-cover rounded-lg border" />)}</div>
              )}

              {/* Assign partner */}
              {["searching_partner", "paid", "partner_assigned"].includes(selected.status) && (
                <div className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-slate-900 flex items-center gap-1"><UserCheck size={16} /> Partner toewijzen</h4>
                    {!matches && <Button size="sm" variant="outline" onClick={loadMatches} data-testid="load-matches-btn">Zoek geschikte partners</Button>}
                  </div>
                  {matches && (
                    <div className="mt-3 space-y-2">
                      {matches.map((p) => (
                        <div key={p.id} className="flex items-center justify-between p-3 rounded-lg border border-slate-200" data-testid={`match-partner-${p.id}`}>
                          <div>
                            <p className="font-medium text-slate-900">{p.company_name}</p>
                            <p className="text-xs text-slate-500">{p.postal_codes.join(", ")} · score {p.match_score} {p.area_match ? "· werkgebied ✓" : ""}</p>
                          </div>
                          <Button size="sm" className="cta-orange rounded-full" disabled={busy} onClick={() => assign(p.id)} data-testid={`assign-partner-${p.id}`}>Toewijzen</Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {selected.work_order && (
                <div className="rounded-lg border border-slate-200 p-4 text-sm">
                  <h4 className="font-semibold text-slate-900 mb-1">Werkbon</h4>
                  <p>{selected.work_order.work_done}</p>
                  <p className="text-slate-500 mt-1">Opgelost: {selected.work_order.problem_solved} · Materiaal: {euro(selected.work_order.material_cost)}</p>
                </div>
              )}

              {selected.invoice && (
                <div className="rounded-lg border border-slate-200 p-4 flex items-center justify-between text-sm">
                  <span className="font-semibold">{selected.invoice.invoice_number}</span>
                  <span className="font-bold">{euro(selected.invoice.total)} (incl. btw)</span>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between gap-2"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-900 text-right">{value}</span></div>;
}
